import React, { useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';

import TextInput from '@components/atoms/text-input/TextInput';
import { useDateRangePickerContext } from '@components/date-range-picker/DateRangePickerContext';
import { parseInputDate } from '@components/date-range-picker/dateRangePickerUtils';
import DateString from '@models/DateString';

function getFormattedDate(ds: DateString | undefined): string {
  return ds ? DateString.format(ds, 'MM/DD/YY') : '';
}

/**
 * Determines whether there is a valid parseable date in the custom input field, and returns the parsed value if so.
 * @param customDate The current custom input date
 * @param backupDate The current date range picker model date
 */
function getParsedDate(customDate?: string, backupDate?: DateString) {
  return customDate
    ? parseInputDate(customDate)
    : backupDate
    ? ({ isValid: true, parsed: backupDate } as const)
    : ({ isValid: false } as const);
}
interface IOwnProps {
  className?: string;
}
const CustomRangeDisplay = ({ className }: IOwnProps): JSX.Element => {
  const {
    startDate,
    endDate,
    minDate,
    maxDate,
    customStartDateInputChanged,
    customEndDateInputChanged,
    customStartDate,
    customEndDate,
  } = useDateRangePickerContext();

  const customStartDateValue = useMemo(
    () => customStartDate.value ?? getFormattedDate(startDate),
    [customStartDate.value, startDate],
  );
  const customEndDateValue = useMemo(
    () => customEndDate.value ?? getFormattedDate(endDate),
    [customEndDate.value, endDate],
  );

  const startDateRef = useRef<HTMLInputElement>(null);
  const endDateRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (startDateRef.current) {
      startDateRef.current.value =
        customStartDate.value ?? getFormattedDate(startDate);
    }
  }, [customStartDate.value, startDate]);

  useEffect(() => {
    if (endDateRef.current) {
      endDateRef.current.value =
        customEndDate.value ?? getFormattedDate(endDate);
    }
  }, [customEndDate.value, endDate]);

  return (
    <Container className={className}>
      <TextInput
        label='Start Date'
        value={customStartDateValue}
        onChange={customStartDateInputChanged}
        onKeyDown={(event) => {
          // If we have a parseable start date here, ArrowUp & ArrowDown should increment and decrement date.
          handleCustomInputKeyDown({
            lastInputValue: customStartDate.value,
            lastModelDate: startDate,
            notifyCustomInputChanged: customStartDateInputChanged,
            event,
            minDate,
            maxDate,
          });
        }}
        placeholder='mm/dd/yy'
        hasError={!customStartDate.isValid}
        ref={startDateRef}
      />
      <TextInput
        label='End Date'
        value={customEndDateValue}
        onChange={customEndDateInputChanged}
        onKeyDown={(event) => {
          handleCustomInputKeyDown({
            lastInputValue: customEndDate.value,
            lastModelDate: endDate,
            notifyCustomInputChanged: customEndDateInputChanged,
            event,
            minDate,
            maxDate,
          });
        }}
        placeholder='mm/dd/yy'
        hasError={!customEndDate.isValid}
        ref={endDateRef}
      />
    </Container>
  );
};

const Container = styled.div(
  ({ theme }) => `
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: ${theme.spacings[3]};
`,
);

const LabelText = styled.div(
  ({ theme }) => `
  padding-bottom: ${theme.spacings[1]};
`,
);

function handleCustomInputKeyDown({
  lastInputValue,
  lastModelDate,
  notifyCustomInputChanged,
  event,
  minDate,
  maxDate,
}: {
  lastInputValue?: string;
  lastModelDate?: DateString;
  notifyCustomInputChanged: (value: string) => void;
  event: React.KeyboardEvent<HTMLInputElement>;
  minDate?: DateString;
  maxDate?: DateString;
}) {
  // If we have a parseable date here, ArrowUp & ArrowDown should increment and decrement date.
  const curDate = getParsedDate(lastInputValue, lastModelDate);
  if (curDate.isValid) {
    const newDate = (() => {
      if (event.key === 'ArrowUp') {
        return DateString.addDays(curDate.parsed, -1);
      } else if (event.key === 'ArrowDown') {
        return DateString.addDays(curDate.parsed, 1);
      }
      return undefined;
    })();
    if (newDate && DateString.isWithin(newDate, { minDate, maxDate })) {
      const newValue = getFormattedDate(newDate);
      event.currentTarget.value = newValue;
      notifyCustomInputChanged(newValue);
    }
  }
}

export default CustomRangeDisplay;
